import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Settings as SettingsIcon, Upload, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import EmailSettings from "@/pages/EmailSettings";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [dashName, setDashName] = useState("");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const logoToken = typeof window !== "undefined" ? localStorage.getItem("ops_token") : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setDoc(data);
      setOrgName(data.organization_name || "");
      setDashName(data.dashboard_name || "");
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/settings", {
        organization_name: orgName,
        dashboard_name: dashName,
      });
      setDoc(data);
      toast.success("Settings saved");
      // Tell other pages to refresh their cached settings
      window.dispatchEvent(new Event("settings-updated"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!isAdmin || !file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: uploaded } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { data } = await api.patch("/settings", { logo_file: uploaded });
      setDoc(data);
      toast.success("Logo updated");
      window.dispatchEvent(new Event("settings-updated"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clearLogo = async () => {
    if (!isAdmin) return;
    setUploading(true);
    try {
      const { data } = await api.patch("/settings", { logo_file: null });
      setDoc(data);
      toast.success("Logo cleared");
      window.dispatchEvent(new Event("settings-updated"));
    } catch (e) {
      toast.error("Failed to clear logo");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading settings…</div>;
  }

  const logoUrl = doc?.logo_file?.id
    ? `${backendUrl}/api/files/${doc.logo_file.id}/download?auth=${logoToken}`
    : null;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <header>
        <div className="label-overline mb-2 flex items-center gap-2"><SettingsIcon className="w-3.5 h-3.5" /> Settings</div>
        <h1 className="text-3xl font-semibold tracking-tight text-white" style={{ fontFamily: "Outfit" }}>
          Settings
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Organization branding shown across the app. Changes apply immediately for everyone.
        </p>
      </header>

      <section className="bg-zinc-900 border border-white/10 rounded-lg p-5 space-y-5">
        <div className="label-overline">Organization Branding</div>

        <div>
          <Label htmlFor="org-name" className="text-xs text-zinc-400">Organization Name</Label>
          <Input
            id="org-name"
            data-testid="settings-org-name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            disabled={!isAdmin}
            placeholder="Digi-One"
            className="mt-1.5 bg-zinc-950 border-white/10"
          />
        </div>

        <div>
          <Label htmlFor="dash-name" className="text-xs text-zinc-400">Dashboard Name</Label>
          <Input
            id="dash-name"
            data-testid="settings-dashboard-name"
            value={dashName}
            onChange={(e) => setDashName(e.target.value)}
            disabled={!isAdmin}
            placeholder="Digi-One Dashboard"
            className="mt-1.5 bg-zinc-950 border-white/10"
          />
          <p className="text-[11px] text-zinc-500 mt-1">Shown in the page title and login screen.</p>
        </div>

        <div>
          <Label className="text-xs text-zinc-400">Organization Logo</Label>
          <div className="mt-1.5 flex items-center gap-4">
            <div className="w-20 h-20 rounded-md border border-white/10 bg-zinc-950 flex items-center justify-center overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="Organization logo" className="max-w-full max-h-full object-contain" data-testid="settings-logo-preview" />
              ) : (
                <ImageIcon className="w-7 h-7 text-zinc-700" />
              )}
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 bg-zinc-950 hover:border-white/30 cursor-pointer text-sm" data-testid="settings-logo-upload">
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading…" : "Upload Logo"}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    disabled={uploading}
                    onChange={(e) => uploadLogo(e.target.files?.[0])}
                  />
                </label>
                {doc?.logo_file && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearLogo}
                    disabled={uploading}
                    data-testid="settings-logo-clear"
                    className="text-zinc-400"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Clear
                  </Button>
                )}
              </div>
            )}
          </div>
          <p className="text-[11px] text-zinc-500 mt-2">Appears in the top-left of the sidebar. Click navigates to the home page. PNG/SVG recommended.</p>
        </div>

        {isAdmin && (
          <div className="flex justify-end pt-2 border-t border-white/5">
            <Button
              onClick={save}
              disabled={saving || !orgName.trim() || !dashName.trim()}
              data-testid="settings-save"
              className="bg-white text-black hover:bg-zinc-200"
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        )}

        {!isAdmin && (
          <p className="text-[11px] text-amber-400 border-t border-white/5 pt-3">
            You&apos;re viewing in read-only mode. Only admins can edit organization settings.
          </p>
        )}
      </section>

      <EmailSettings isAdmin={isAdmin} />
    </div>
  );
}
